import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiClock, FiFacebook, FiInstagram, FiLinkedin } from 'react-icons/fi';
import { useReservation } from '../contexts/ReservationContext';
import Logo from './Logo';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { open: openReservation } = useReservation();

  const quickLinks = [
    { label: 'Accueil', href: '#accueil' },
    { label: 'Services', href: '#services' },
    { label: 'Véhicules', href: '#vehicules' },
    { label: 'Témoignages', href: '#temoignages' },
    { label: 'À Propos', href: '#a-propos' },
    { label: 'Réservation', href: '#reservation', action: 'modal' }
  ];

  const services = [
    { label: 'Limousine Mariage Paris', href: '/limousine-mariage-paris' },
    { label: 'Transfert Roissy CDG', href: '/transfert-aeroport-roissy-cdg' },
    { label: 'Transfert Aéroport Orly', href: '/transfert-aeroport-orly' },
    { label: 'Limousine EVJF Paris', href: '/limousine-evjf-paris' },
    { label: 'Limousine Disneyland Paris', href: '/limousine-disneyland-paris' },
    { label: 'Limousine Versailles', href: '/limousine-versailles' },
    { label: 'Limousine Anniversaire', href: '/limousine-anniversaire-paris' },
    { label: 'Limousine Bal de Promo', href: '/limousine-bal-de-promo-paris' }
  ];

  const vehicles = [
    { label: 'Rolls Royce Phantom', href: '/location-rolls-royce-phantom-paris' },
    { label: 'Hummer H3 Stretch', href: '/location-hummer-stretch-paris' },
    { label: 'Chrysler 300 Stretch', href: '/location-chrysler-300-stretch-paris' },
    { label: 'Mercedes Classe S', href: '/location-mercedes-classe-s-paris' },
    { label: 'Citroën 2CV Vintage', href: '/location-citroen-2cv-mariage' }
  ];

  const departements = [
    { label: 'Hauts-de-Seine 92', href: '/limousine-hauts-de-seine-92' },
    { label: 'Seine-Saint-Denis 93', href: '/limousine-seine-saint-denis-93' },
    { label: 'Val-de-Marne 94', href: '/limousine-val-de-marne-94' },
    { label: 'Yvelines 78', href: '/limousine-yvelines-78' },
    { label: 'Val-d\'Oise 95', href: '/limousine-val-doise-95' },
    { label: 'Essonne 91', href: '/limousine-essonne-91' },
    { label: 'Seine-et-Marne 77', href: '/limousine-seine-et-marne-77' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <footer className="footer">
      <div className="footer-main">
        <div className="container">
          <div className="footer-content">
            {/* Logo et description */}
            <div className="footer-brand">
              <Link to="/" className="footer-logo" aria-label="VIP Limousine Paris — Accueil">
                <Logo />
              </Link>
              
              <p className="footer-description">
                Service premium de transport de luxe à Paris. Excellence, élégance et 
                professionnalisme pour tous vos déplacements d'exception depuis plus de 15 ans.
              </p>
              
              <div className="footer-social">
                <a href="#" className="social-link" aria-label="Facebook">
                  <FiFacebook />
                </a>
                <a href="#" className="social-link" aria-label="Instagram">
                  <FiInstagram />
                </a>
                <a href="#" className="social-link" aria-label="LinkedIn">
                  <FiLinkedin />
                </a>
              </div>
            </div>

            {/* Liens rapides */}
            <div className="footer-section">
              <h3 className="section-title">Navigation</h3>
              <ul className="footer-links">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      onClick={() =>
                        link.action === 'modal'
                          ? openReservation()
                          : scrollToSection(link.href)
                      }
                      className="footer-link"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div className="footer-section">
              <h3 className="section-title">Nos Services à Paris</h3>
              <ul className="footer-links">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.href}
                      className="footer-link"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    >
                      {service.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Véhicules */}
            <div className="footer-section">
              <h3 className="section-title">Nos Véhicules</h3>
              <ul className="footer-links">
                {vehicles.map((v, index) => (
                  <li key={index}>
                    <Link
                      to={v.href}
                      className="footer-link"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    >
                      {v.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link to="/blog" className="footer-link" onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}>
                    Blog & Conseils
                  </Link>
                </li>
              </ul>
            </div>

            {/* Départements */}
            <div className="footer-section">
              <h3 className="section-title">Île-de-France</h3>
              <ul className="footer-links">
                {departements.map((d, index) => (
                  <li key={index}>
                    <Link
                      to={d.href}
                      className="footer-link"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                    >
                      {d.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div className="footer-section">
              <h3 className="section-title">Contact</h3>
              <div className="contact-info">
                <div className="contact-item">
                  <FiPhone className="contact-icon" />
                  <div>
                    <a href="tel:+33661630113" className="contact-link">
                      06 61 63 01 13
                    </a>
                    <span className="contact-label">Réservations & Urgences</span>
                  </div>
                </div>
                
                <div className="contact-item">
                  <FiMail className="contact-icon" />
                  <div>
                    <a href="mailto:contact@limousine-vip-paris.fr" className="contact-link">
                      contact@limousine-vip-paris.fr
                    </a>
                    <span className="contact-label">Devis & Informations</span>
                  </div>
                </div>
                
                <div className="contact-item">
                  <FiMapPin className="contact-icon" />
                  <div>
                    <span className="contact-text">Paris & Région Parisienne</span>
                    <span className="contact-label">Zone de service</span>
                  </div>
                </div>
                
                <div className="contact-item">
                  <FiClock className="contact-icon" />
                  <div>
                    <span className="contact-text">24h/24 - 7j/7</span>
                    <span className="contact-label">Service disponible</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Informations légales */}
      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            <div className="copyright">
              <p>&copy; {currentYear} VIP Limousine Paris. Tous droits réservés.</p>
            </div>
            
            <div className="legal-links">
              <Link to="/mentions-legales" className="legal-link">
                Mentions légales
              </Link>
              <Link to="/politique-confidentialite" className="legal-link">
                Politique de confidentialité
              </Link>
              <Link to="/cgv" className="legal-link">
                CGV
              </Link>
            </div>
            
            <div className="footer-badges">
              <div className="badge">
                <span className="badge-text">Service Premium</span>
              </div>
              <div className="badge">
                <span className="badge-text">Disponible 24/7</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de retour en haut */}
      <button 
        className="back-to-top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Retour en haut de page"
      >
        ↑
      </button>
    </footer>
  );
};

export default Footer;
